import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';
import { Buffer } from 'buffer';
/**
 * Extracts the signature from a signed Solana transaction
 * @param transactionBase58 - Base58-encoded signed transaction
 * @returns Base58-encoded signature
 */
export function extractSignatureFromTransaction(transactionBase58) {
    const transactionBytes = bs58.decode(transactionBase58);
    const transaction = Transaction.from(transactionBytes);
    const signature = transaction.signature;
    if (!signature) {
        throw new Error('Transaction has no signature');
    }
    return bs58.encode(new Uint8Array(signature));
}
/**
 * Extracts signatures from multiple signed Solana transactions
 * @param transactionsBase58 - Array of base58-encoded signed transactions
 * @returns Array of base58-encoded signatures
 */
export function extractSignaturesFromTransactions(transactionsBase58) {
    return transactionsBase58.map(tx => extractSignatureFromTransaction(tx));
}
export function convertBase64ToBase58(valueBase64) {
    const padded = valueBase64 + '='.repeat((4 - (valueBase64.length % 4)) % 4);
    const decoded = Buffer.from(padded, 'base64');
    return bs58.encode(new Uint8Array(decoded));
}
export function decodeBase64ToBytes(valueBase64) {
    const padded = valueBase64 + '='.repeat((4 - (valueBase64.length % 4)) % 4);
    const decoded = Buffer.from(padded, 'base64');
    return new Uint8Array(decoded);
}
export function isLikelyBase58(value) {
    return /^[1-9A-HJ-NP-Za-km-z]+$/.test(value);
}
export function isValidSolanaPubkey(value) {
    try {
        // eslint-disable-next-line no-new
        new PublicKey(value);
        return true;
    }
    catch {
        return false;
    }
}
export function isVersionedTransactionBytes(bytes) {
    return bytes.length > 0 && (bytes[0] & 0x80) !== 0;
}
export function extractFeePayerFromTransactionString(tx) {
    const bytes = isLikelyBase58(tx) ? bs58.decode(tx) : decodeBase64ToBytes(tx);
    return extractFeePayerFromTransactionBytes(bytes);
}
export function extractFeePayerFromTransactionBytes(bytes) {
    if (bytes.length === 0)
        return null;
    try {
        if (isVersionedTransactionBytes(bytes)) {
            const vtx = VersionedTransaction.deserialize(bytes);
            const feePayer = vtx.message.staticAccountKeys?.[0];
            return feePayer ? feePayer.toBase58() : null;
        }
        const legacyTx = Transaction.from(bytes);
        const compiled = legacyTx.compileMessage();
        const feePayer = legacyTx.feePayer ?? compiled.accountKeys?.[0];
        return feePayer ? feePayer.toBase58() : null;
    }
    catch {
        return null;
    }
}
// Extracts a Solana address from a single params object.
// - If `pubkey` exists, return it.
// - Otherwise, derive from `transaction` or first of `transactions`.
function extractSolanaAddressFromObject(obj) {
    // Prefer explicit `pubkey` when available
    if (typeof obj.pubkey === 'string') {
        return obj.pubkey;
    }
    // Otherwise use serialized `transaction` (base58 or base64)
    if (typeof obj.transaction === 'string') {
        return extractFeePayerFromTransactionString(obj.transaction);
    }
    // If multiple transactions are present, scan for the first decodable fee payer
    if (Array.isArray(obj.transactions)) {
        for (const tx of obj.transactions) {
            if (typeof tx === 'string') {
                const maybe = extractFeePayerFromTransactionString(tx);
                if (maybe)
                    return maybe;
            }
        }
    }
    return null;
}
/**
 * Extracts a Solana address from request params.
 * Supports object params or the first element of array params.
 */
export function extractSolanaAddressFromParams(params) {
    if (params && !Array.isArray(params) && typeof params === 'object') {
        return extractSolanaAddressFromObject(params);
    }
    if (Array.isArray(params) && params[0] && typeof params[0] === 'object') {
        return extractSolanaAddressFromObject(params[0]);
    }
    return null;
}
//# sourceMappingURL=SolanaUtils.js.map